import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
//import Dashboard from './dashboard/Dashboard';
import TeacherDashboard from './dashboard/TeacherDashboard';
import DashboardLayout from './dashboard/DashboardLayout';
import TakeAttendancePage from './attendance/TakeAttendancePage';
import ViewAttendancePage from './attendance/ViewAttendancePage';
import AttendanceReportsPage from './attendance/AttendanceReportsPage';
import ClassManagementPage from './class_management/ClassManagement';
import StudentManagementPage from './student_management/StudentManagementPage';
import SchoolStudentManagementPage from './student_management/SchoolStudentManagementPage';
import StudentTrackingDashboard from './student_tracking/StudentTrackingDashboard';
import ClassTrackingDashboard from './class_tracking/ClassTrackingDashboard';
import SchoolOverviewDashboard from './school_overview/SchoolOverviewDashboard';
import AccountSettings from './settings/AccountSettings';
import ProfileSettings from './settings/ProfileSettings';
import AcceptInvite from './auth/AcceptInvite';
import AcceptInviteComplete from './auth/AcceptInviteComplete';
import OrganizationDeleted from './auth/OrganizationDeleted';
import LandingPage from './landing_page/LandingPage';
import SignInPage from './auth/SignInPage';
import RegisterPage from './auth/RegisterPage';
import AuthCallback from './auth/AuthCallback';
import Onboarding from './onboarding/onboarding';
import { API_BASE_URL } from '../config';
import AdminPanel from './dashboard/AdminPanel';
import SchoolGuard from './guards/SchoolGuard';
import PermissionGuard from './guards/PermissionGuard';
import OrganizationRemovalGuard from './guards/OrganizationRemovalGuard';
import MaintenanceGuard from './guards/MaintenanceGuard';
import MaintenancePage from './maintenance/MaintenancePage';
import { TenantProvider } from '../contexts/TenantContext';
import ProtectedRoute from './routing/ProtectedRoute';

function AppRoutes() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use a ref to track if we've already navigated in this render cycle
  const hasNavigated = React.useRef(false);
  
  useEffect(() => {
    // Reset navigation tracker on location change
    hasNavigated.current = false;
  }, [location]);
  
  useEffect(() => {
    // Ensure user has organization and check onboarding status
    const ensureUserSetup = async () => {
      // Don't run this check on invitation pages, callback, or specific authenticated routes
      if (location.pathname === '/auth/accept-invite' ||
          location.pathname === '/auth/accept-invite-complete' ||
          location.pathname === '/callback') {
        return;
      }
      
      // Only run setup check for root path or first time authentication
      // Don't run on every route change within the app
      if (isAuthenticated && !hasNavigated.current && location.pathname === '/') {
        try {
          
          // Check if we should skip organization creation (e.g., after deletion)
          const skipOrgCreation = sessionStorage.getItem('skip_org_creation');
          if (skipOrgCreation) {
            console.log('🚫 Skipping auto-organization creation, redirecting to onboarding');
            sessionStorage.removeItem('skip_org_creation');
            hasNavigated.current = true;
            navigate('/onboarding', { replace: true });
            return;
          }
          
          // First, ensure user has an organization (create if needed)
          console.log('🔄 Ensuring user has organization...');
          
          try {
            const token = await getAccessTokenSilently();
            const response = await fetch(`${API_BASE_URL}/api/organizations/ensure`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const organization = await response.json();
              console.log('✅ Organization ensured for user:', organization.name);
            } else {
              const error = await response.json();
              console.error('❌ Failed to ensure organization:', error);
            }
          } catch (orgError) {
            console.error('❌ Organization ensure error:', orgError);
            // Continue even if organization ensuring fails
          }
          
          // If we're on the root path, redirect to user's organization dashboard
          if (location.pathname === '/') {
            console.log('✅ User setup complete, fetching organization for redirect...');
            
            try {
              // Get user's organization for proper redirect
              const token = await getAccessTokenSilently();
              const orgResponse = await fetch(`${API_BASE_URL}/api/organizations/me`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              if (orgResponse.ok) {
                const organizations = await orgResponse.json();
                let targetOrg = null;
                
                if (Array.isArray(organizations) && organizations.length > 0) {
                  targetOrg = organizations[0];
                } else if (organizations && organizations.unique_id) {
                  targetOrg = organizations;
                }
                
                if (targetOrg && targetOrg.unique_id) {
                  hasNavigated.current = true;
                  navigate(`/org/${targetOrg.unique_id}/dashboard`, { replace: true });
                  return;
                }
              }
            } catch (orgFetchError) {
              console.error('Failed to fetch organization for redirect:', orgFetchError);
            }
            
            // Fallback: if we can't get organization info, stay on landing page
            console.log('ℹ️ Could not fetch organization for user, staying on landing page');
          }
        } catch (err) {
          console.error("Error in user setup:", err);
        }
      }
    };
    
    ensureUserSetup();
  }, [isAuthenticated, getAccessTokenSilently, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Note: Removed global loading screen to prevent sidebar flickering
  // The StaticSidebar and TenantContext now handle loading gracefully

  return (
    <Routes>
      {/* All routes wrapped in MaintenanceGuard */}
      <Route path="*" element={
        <MaintenanceGuard>
          <Routes>
      {/* Maintenance page - guard handles redirects */}
      <Route path="/maintenance" element={<MaintenancePage />} />
      <Route path="/" element={<LandingPage />} />
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/organization-deleted" element={<OrganizationDeleted />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding 
            onComplete={async (finalData) => {
              // Mark onboarding as completed
              localStorage.setItem('onboardingCompleted', 'true');
              
              // Get the user's organization and redirect to specific dashboard
              try {
                const token = await getAccessTokenSilently();
                const orgResponse = await fetch(`${API_BASE_URL}/api/organizations/me`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (orgResponse.ok) {
                  const organizations = await orgResponse.json();
                  let targetOrg = null;
                  
                  if (Array.isArray(organizations) && organizations.length > 0) {
                    targetOrg = organizations[0];
                  } else if (organizations && organizations.unique_id) {
                    targetOrg = organizations;
                  }
                  
                  if (targetOrg && targetOrg.unique_id) {
                    // Redirect to organization dashboard
                    navigate(`/org/${targetOrg.unique_id}/dashboard`, { replace: true });
                    return;
                  }
                }
              } catch (error) {
                console.error('Failed to fetch organization after onboarding:', error);
              }
              
              // Fallback to root if organization fetch fails
              navigate('/', { replace: true });
            }}
            onSkip={async () => {
              // Mark onboarding as completed
              localStorage.setItem('onboardingCompleted', 'true');
              
              // Auto-create organization for users who skip onboarding
              try {
                console.log('🔄 Auto-creating organization for user who skipped onboarding...');
                const token = await getAccessTokenSilently();
                const response = await fetch(`${API_BASE_URL}/api/organizations/ensure`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                });

                if (response.ok) {
                  const organization = await response.json();
                  console.log('✅ Organization auto-created:', organization.name);
                  
                  // Redirect to the new organization dashboard
                  if (organization.unique_id) {
                    navigate(`/org/${organization.unique_id}/dashboard`, { replace: true });
                    return;
                  }
                } else {
                  const error = await response.json();
                  console.error('❌ Failed to auto-create organization:', error);
                }
              } catch (orgError) {
                console.error('❌ Organization auto-creation error:', orgError);
              }
              
              // Fallback to root if organization creation fails
              navigate('/', { replace: true });
            }}
          />
        </ProtectedRoute>
      } />
      
      {/* New tenant-aware routes with persistent layout */}
      <Route path="/org/:orgUniqueId/*" element={
        <ProtectedRoute>
          <OrganizationRemovalGuard>
            <TenantProvider>
              <DashboardLayout>
                <Routes>
                <Route path="dashboard" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    {/*<Dashboard />*/}
                    <ClassManagementPage />
                  </SchoolGuard>
                } />
                <Route path="admin" element={<AdminPanel />} />
                <Route path="teacher" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    <TeacherDashboard />
                  </SchoolGuard>
                } />
                <Route path="class-management" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    <ClassManagementPage />
                  </SchoolGuard>
                } />
                <Route path="/attendance/:classId" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    <TakeAttendancePage />
                  </SchoolGuard>
                } />
                <Route path="students" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    <PermissionGuard resource="student" action="read" organizationWide={true}>
                      <StudentManagementPage />
                    </PermissionGuard>
                  </SchoolGuard>
                } />
                <Route path="student-tracking" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    <StudentTrackingDashboard />
                  </SchoolGuard>
                } />
                <Route path="student-tracking/:studentId" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    <StudentTrackingDashboard />
                  </SchoolGuard>
                } />
                <Route path="class-tracking" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    <ClassTrackingDashboard />
                  </SchoolGuard>
                } />
                <Route path="school-overview" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    <SchoolOverviewDashboard />
                  </SchoolGuard>
                } />
                <Route path="/take-attendance" element={<Navigate to="/class-management" replace />} />
                <Route path="/view-attendance" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                <ViewAttendancePage />
                </SchoolGuard>
                } />
                <Route path="view-attendance" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    <ViewAttendancePage />
                  </SchoolGuard>
                } />
                <Route path="attendance-reports" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    <AttendanceReportsPage />
                  </SchoolGuard>
                } />
                <Route path="account-settings" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    <AccountSettings />
                  </SchoolGuard>
                } />
                <Route path="profile-settings" element={
                  <SchoolGuard allowedPaths={['/admin', '/users', '/billing']}>
                    <ProfileSettings />
                  </SchoolGuard>
                } />
                <Route path="school/:schoolUniqueId/dashboard" element={
                  <SchoolGuard>
                    {/* <Dashboard /> */}
                    <ClassManagementPage />
                  </SchoolGuard>
                } />
                <Route path="school/:schoolUniqueId/teacher" element={
                  <SchoolGuard>
                    <TeacherDashboard />
                  </SchoolGuard>
                } />
                <Route path="school/:schoolUniqueId/class-management" element={
                  <SchoolGuard>
                    <ClassManagementPage />
                  </SchoolGuard>
                } />
                <Route path="school/:schoolUniqueId/students" element={
                  <SchoolGuard>
                    <SchoolStudentManagementPage />
                  </SchoolGuard>
                } />
                <Route path="school/:schoolUniqueId/student-tracking" element={
                  <SchoolGuard>
                    <StudentTrackingDashboard />
                  </SchoolGuard>
                } />
                <Route path="school/:schoolUniqueId/student-tracking/:studentId" element={
                  <SchoolGuard>
                    <StudentTrackingDashboard />
                  </SchoolGuard>
                } />
                <Route path="school/:schoolUniqueId/class-tracking" element={
                  <SchoolGuard>
                    <ClassTrackingDashboard />
                  </SchoolGuard>
                } />
                <Route path="school/:schoolUniqueId/school-overview" element={
                  <SchoolGuard>
                    <SchoolOverviewDashboard />
                  </SchoolGuard>
                } />
                <Route path="school/:schoolUniqueId/take-attendance" element={
                  <SchoolGuard>
                    <TakeAttendancePage />
                  </SchoolGuard>
                } />
                <Route path="school/:schoolUniqueId/view-attendance" element={
                  <SchoolGuard>
                    <ViewAttendancePage />
                  </SchoolGuard>
                } />
                <Route path="school/:schoolUniqueId/attendance-reports" element={
                  <SchoolGuard>
                    <AttendanceReportsPage />
                  </SchoolGuard>
                } />
              </Routes>
            </DashboardLayout>
          </TenantProvider>
          </OrganizationRemovalGuard>
        </ProtectedRoute>
      } />
      
      {/* Legacy dashboard route - shows login required for unauthenticated users */}

      <Route path="/auth/accept-invite" element={<AcceptInvite />} />
      <Route path="/auth/accept-invite-complete" element={<AcceptInviteComplete />} />
      <Route path="/callback" element={<AuthCallback />} />
          </Routes>
        </MaintenanceGuard>
      } />
    </Routes>
  );
}

export default AppRoutes;